import { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from "uuid";
import CommandCenterService from '../../../service/CommandCenterService';
import MissionService from '../../../service/MissionService';
import useTrueTwin from './useTrueTwin';
import useUrlParams from "../hooks/useUrlParams";
import { showToastMessage } from '../../../utils/commonFunctions';
import useScenario from './useScenario';
import { CommandCenterStateProviderContext } from '../context';
import { isOperateMode } from './utils';



const useConsole = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { scenario } = useScenario();

    const context = useContext(CommandCenterStateProviderContext);
    if (context === undefined) {
        throw new Error('useSatellite must be used within a Satellite Provider');
    }

    const [busComponentOptions, setBusComponentOptions] = useState<any>([])
    const [busCommandOptions, setBusCommandOptions] = useState<any>([])
    const [allBusCommands, setAllBusCommands] = useState<any>([])
    const [allPayloadComponents, setAllPayloadComponents] = useState<any>([])
    const [commandDetails, setCommandDetails] = useState<any | null>(null);
    const [formData, setFormData] = useState<any | null>(null);
    const [linkId, setLinkId] = useState<string | null>(null);
    const { truetwin_id } = useTrueTwin()
    const { highlightedCommands, setHighlightedCommands } = context

    const { mission_id, scenario_id, dashboard, sat_id, sat_grp_id } = useUrlParams()

    const fetchBusComponents = async () => {
        try {
            const res: any = await CommandCenterService.getObject(truetwin_id);
            if (res.data) {
                const response: any[] = res.data;

                // 1. Deduplicate funcCategory using Map
                const categoryMap = new Map<string, any>();
                response.forEach((m) => {
                    const category = m.funcCategory;
                    if (category && !categoryMap.has(category)) {
                        categoryMap.set(category, m);
                    }
                });

                // 2. Create component options starting with 'All'
                const componentOptions = Array.from(categoryMap.values()).map((cat) => ({
                    label: cat.funcCategory,
                    value: cat.funcCategory,
                }));
                setBusComponentOptions([{ label: 'All', value: 'All' }, ...componentOptions]);

                // 3. Deduplicate commands based on commandId + name
                const commandMap = new Map<string, any>();
                response.forEach((command) => {
                    const label = `${command.sequenceData?.commandId}-${command.name}`;
                    if (label && !commandMap.has(label)) {
                        commandMap.set(label, {
                            label,
                            value: command
                        });
                    }
                });

                const busCommands = Array.from(commandMap.values());
                setBusCommandOptions(busCommands);
                setAllBusCommands(busCommands); // Store for filtering
            }
        } catch (err) {
            console.log(err);
        }
    };

    const fetchBusCommands = (component: string) => {
        const filtered = component === 'All'
            ? allBusCommands
            : allBusCommands.filter(cmd => cmd.value.funcCategory === component);
        setBusCommandOptions(filtered);
    };

    //Highlight the TCs which are executed
    const addCommandUidToHighlight = (commandUid) => {
        setHighlightedCommands(prev => {
            const next = new Set(prev);
            next.add(commandUid);
            return next;
        });

        // Auto-remove after 30 seconds
        setTimeout(() => {
            setHighlightedCommands(prev => {
                const next = new Set(prev);
                next.delete(commandUid);
                return next;
            });
        }, 30000);
    }

    //Remove the highlight
    const removeCommandUidForHighlight = (commandUid) => {
        setHighlightedCommands(prev => {
            const next = new Set(prev);
            next.delete(commandUid);
            return next;
        });
    }

    const fetchPayloadComponents = async () => {
        if (!sat_grp_id && !mission_id && (scenario?.scenarioDeploymentType === 'SIMULATE' || scenario?.scenarioDeploymentType === 'OPERATE')) return;
        setLoading(true);
        try {
            const payloadResponse = await MissionService.get_payload_by_sat_id(sat_grp_id, mission_id)
            const all_payloads = payloadResponse?.data || []
            if (!all_payloads?.length) return
            const configuredPayloads = scenario?.remoteAppConfiguration?.remoteApp?.AppData || []
            if (!configuredPayloads?.length) return;
            const all_payload_components: any = []
            all_payloads.forEach((payload) => {
                const configuredPayload = configuredPayloads.find((configuredPayload) => configuredPayload?.['id'] === payload?.uniqueId)
                if (!configuredPayload) return
                const selected_payload = {
                    label: configuredPayload?.['name'],
                    value: {
                        payload: configuredPayload,
                        sequence: payload?.sequences || []
                    }
                }
                all_payload_components.push(selected_payload)
            })
            setAllPayloadComponents(all_payload_components)

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const operateMode = isOperateMode(dashboard, scenario)

    useEffect(() => {
        if (truetwin_id) {
            fetchBusComponents();
            if (!operateMode) {
                fetchPayloadComponents();
            }
        }
    }, [truetwin_id, operateMode])

    useEffect(() => {
        if (sat_grp_id && mission_id && scenario) {
            fetchPayloadComponents();
        }
    }, [sat_grp_id, mission_id, scenario])

    const postData = async (payload: any): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            if (operateMode) {
                payload[0].LinkId = linkId
            }
            payload.forEach((item) => {
                item.CommandUid = uuidv4();
                const commandUid = item.CommandUid;
                addCommandUidToHighlight(commandUid)
            });
            setLoading(true);
            setError(null);

            try {
                await CommandCenterService.postCommandDetails(payload);
                const currentDataEvent = new Event('loadCurrentData');
                window.dispatchEvent(currentDataEvent);
                setLoading(false);
                resolve();
            } catch (err: any) {

                setError(err.message);
                setLoading(false);
                reject(err);
            }
        });
    };

    const getCommandDetails = async (CommandId) => {
        setLoading(true);
        try {
            const res = await CommandCenterService.getCommandDetailsById(truetwin_id, sat_id, scenario_id, CommandId?.['CommandUid']);
            setCommandDetails(res);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };


    const uploadAPI = (bandType, file_data, time, groundStationId) => {
        return new Promise((resolve, reject) => {
            file_data.append("scenarioId", scenario_id as string);
            file_data.append("trueTwinId", truetwin_id as string);
            file_data.append("bandType", bandType);
            file_data.append("insertTime", time);
            file_data.append("groundStationId", groundStationId);

            CommandCenterService.postUploadFileConsoleFlatSatTele(file_data)
                .then((response) => {
                    if (response?.data?.message && response?.data?.message.toLowerCase().includes('error')) {
                        showToastMessage('error', response?.data?.message);
                        reject(new Error(response?.data?.message));
                    } else if (response?.data?.message) {
                        showToastMessage('success', response?.data?.message);
                        resolve(response);
                    } else if (response) {
                        showToastMessage('success', 'File uploaded successfully');
                        resolve(response);
                    } else {
                        showToastMessage('error', 'File upload failed');
                        reject(new Error('File upload failed'));
                    }
                })
                .catch((error) => {
                    const message = error?.response?.data?.message;
                    showToastMessage('error', message || 'Error uploading file. Please try again.');
                    reject(error);
                });
        });
    };

    const exportMessages = async () => {
        let session_id = sessionStorage.getItem('sessionId');
        if (!session_id || session_id.trim() === '') {
            session_id = String(uuidv4());
            sessionStorage.setItem('sessionId', session_id);
        }
        const credentials = sessionStorage.getItem("authentication");


        try {
            const res = await CommandCenterService.exportConsoleMessages(
                sat_id,
                truetwin_id,
                scenario_id,
                session_id,
                credentials
            );

            if (res) {
                const url = res
                window.location.href = url;
                showToastMessage('success', 'Messages exported successfully');
            }
        } catch (err) {
            console.error('Failed to export messages:', err);
            showToastMessage('error', 'Failed to export messages');
        }
    };



    return {
        loading, error, postData, fetchBusCommands, busComponentOptions, busCommandOptions, allBusCommands, allPayloadComponents, getCommandDetails, commandDetails, setFormData, formData, uploadAPI, exportMessages, highlightedCommands, setHighlightedCommands, addCommandUidToHighlight, removeCommandUidForHighlight

    };
};

export default useConsole;